import { useState, useRef } from "react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Upload, FileSpreadsheet, Download, CheckCircle2, AlertTriangle, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, color }) => (
  <div className={`rounded-lg border p-3 ${color}`} data-testid={`hr-stat-${label}`}>
    <div className="text-2xl font-bold leading-none">{value}</div>
    <div className="text-[11px] mt-1 opacity-80">{label}</div>
  </div>
);

const RowsTable = ({ data, mode }) => (
  <div className="overflow-auto max-h-[40vh] border rounded-md">
    <table className="w-full text-xs">
      <thead className="bg-gray-50 sticky top-0">
        <tr className="text-left text-gray-600">
          <th className="px-2 py-2">Operatore</th>
          <th className="px-2 py-2">CF</th>
          {mode !== "nontrovati" && <th className="px-2 py-2">Coordinatore</th>}
          {mode !== "nontrovati" && <th className="px-2 py-2">Contratto</th>}
          <th className="px-2 py-2">Avvisi</th>
        </tr>
      </thead>
      <tbody>
        {data.map((r, i) => (
          <tr key={i} className="border-t hover:bg-gray-50" data-testid="hr-row">
            <td className="px-2 py-1.5 font-medium whitespace-nowrap">{r.cognome} {r.nome}
              {r.match_method === "nome" && <span className="ml-1 text-[10px] text-amber-600">(per nome)</span>}
            </td>
            <td className="px-2 py-1.5 text-gray-500 font-mono text-[10px]">{r.cf}</td>
            {mode !== "nontrovati" && (
              <td className="px-2 py-1.5">
                {r.proposed_coordinatore ? (
                  <span className={r.will_update_coordinatore ? "text-emerald-700 font-medium" : "text-gray-400"}>
                    {r.current_coordinatore ? `${r.current_coordinatore} → ` : ""}{r.proposed_coordinatore}
                    {r.will_update_coordinatore && <CheckCircle2 className="inline w-3 h-3 ml-1 text-emerald-600" />}
                  </span>
                ) : <span className="text-gray-300">—</span>}
              </td>
            )}
            {mode !== "nontrovati" && (
              <td className="px-2 py-1.5">
                {r.proposed_contratto ? (
                  <span className={r.will_update_contratto ? "text-emerald-700 font-medium" : "text-gray-400"}>
                    {r.proposed_contratto}{r.proposed_ore_mensili ? ` (${r.proposed_ore_mensili}h)` : ""}
                    {r.will_update_contratto && <CheckCircle2 className="inline w-3 h-3 ml-1 text-emerald-600" />}
                  </span>
                ) : <span className="text-gray-300">—</span>}
              </td>
            )}
            <td className="px-2 py-1.5">
              {(r.warnings || []).length > 0 ? (
                <span className="text-amber-700 text-[11px]">{(r.warnings || []).join(" · ")}</span>
              ) : <span className="text-gray-300">—</span>}
            </td>
          </tr>
        ))}
        {data.length === 0 && (
          <tr><td colSpan={5} className="px-2 py-6 text-center text-gray-400">Nessun record</td></tr>
        )}
      </tbody>
    </table>
  </div>
);

export default function HRImportPanel({ open, onClose }) {
  const [file, setFile] = useState(null);
  const [commessa, setCommessa] = useState("PESCARA");
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null); // { import_id, stats, rows }
  const [applied, setApplied] = useState(false);
  const fileRef = useRef(null);

  const COMMESSE = ["PESCARA", "CHIETI", "TERAMO", "TUTTE"];

  const reset = () => { setFile(null); setResult(null); setApplied(false); if (fileRef.current) fileRef.current.value = ""; };

  const handleClose = () => { reset(); onClose(); };

  const handlePreview = async () => {
    if (!file) { toast.error("Seleziona un file Excel"); return; }
    setLoading(true);
    setResult(null);
    setApplied(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("commessa", commessa);
      const res = await axios.post(`${API}/admin/hr-import/preview`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(`Anteprima generata: ${res.data.stats.totale_filtrati} operatori (${commessa})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'anteprima");
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    try {
      const res = await axios.get(`${API}/admin/hr-import/${result.import_id}/excel`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `import_hr_pescara_dryrun.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Errore durante il download");
    }
  };

  const handleApply = async () => {
    if (!result) return;
    if (!window.confirm("Confermi l'applicazione delle modifiche? Verranno aggiornati SOLO i campi attualmente vuoti. L'operazione non è reversibile automaticamente.")) return;
    setApplying(true);
    try {
      const res = await axios.post(`${API}/admin/hr-import/${result.import_id}/apply`);
      setApplied(true);
      toast.success(`Applicato: ${res.data.coordinatori_aggiornati} coordinatori, ${res.data.organigramma_aggiornati} in organigramma, ${res.data.contratti_aggiornati} contratti`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante l'applicazione");
    } finally {
      setApplying(false);
    }
  };

  const stats = result?.stats;
  const rows = result?.rows || [];
  const trovati = rows.filter((r) => r.status === "trovato");
  const aggiornabili = trovati.filter((r) => r.will_update_coordinatore || r.will_update_contratto);
  const daVerificare = trovati.filter((r) => (r.warnings || []).length > 0);
  const nonTrovati = rows.filter((r) => r.status === "non_trovato");

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) handleClose(); }}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto" data-testid="hr-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-cyan-600" />
            Importa Dati HR
          </DialogTitle>
          <DialogDescription className="text-xs">
            {`Carica il file Excel HR e scegli la commessa/ASL. Verrà generata un'anteprima (dry-run) dei soli operatori della commessa selezionata. Nessun dato viene scritto finché non premi "Applica". Il coordinatore indicato viene impostato anche come posizione in Organigramma (manager). Coordinatori, contratti e posizioni già presenti NON vengono sovrascritti (modificabili a mano dall'Organigramma).`}
          </DialogDescription>
        </DialogHeader>

        {/* Upload + selettore commessa */}
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center border rounded-lg p-3 bg-gray-50">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setApplied(false); }}
            className="text-xs flex-1"
            data-testid="hr-file-input"
          />
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-gray-500">Commessa:</span>
            <select
              value={commessa}
              onChange={(e) => { setCommessa(e.target.value); setResult(null); setApplied(false); }}
              className="h-9 px-2 text-xs rounded-md border border-gray-200 bg-white"
              data-testid="hr-commessa-select"
            >
              {COMMESSE.map((c) => <option key={c} value={c}>{c === "TUTTE" ? "Tutte" : c}</option>)}
            </select>
          </div>
          <Button onClick={handlePreview} disabled={loading || !file} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white gap-1.5" data-testid="hr-preview-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Genera Anteprima
          </Button>
        </div>

        {stats && (
          <>
            <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
              <StatCard label={stats.filtro || "Filtrati"} value={stats.totale_filtrati} color="bg-cyan-50 border-cyan-200 text-cyan-800" />
              <StatCard label="Trovati" value={stats.trovati} color="bg-emerald-50 border-emerald-200 text-emerald-800" />
              <StatCard label="Non trovati" value={stats.non_trovati} color="bg-rose-50 border-rose-200 text-rose-800" />
              <StatCard label="Coord. da agg." value={stats.agg_coordinatore} color="bg-violet-50 border-violet-200 text-violet-800" />
              <StatCard label="Organigramma" value={stats.agg_organigramma ?? 0} color="bg-indigo-50 border-indigo-200 text-indigo-800" />
              <StatCard label="Contr. da agg." value={stats.agg_contratto} color="bg-violet-50 border-violet-200 text-violet-800" />
              <StatCard label="Da compilare" value={stats.coord_mancante_file + stats.ore_mancanti_file} color="bg-amber-50 border-amber-200 text-amber-800" />
            </div>

            <div className="flex flex-wrap gap-2 items-center justify-between">
              <Button onClick={handleDownload} variant="outline" size="sm" className="gap-1.5 border-cyan-300 text-cyan-700" data-testid="hr-download-btn">
                <Download className="w-4 h-4" /> Scarica Report Excel
              </Button>
              {applied ? (
                <span className="flex items-center gap-1.5 text-emerald-700 text-sm font-medium" data-testid="hr-applied-msg">
                  <CheckCircle2 className="w-4 h-4" /> Modifiche applicate
                </span>
              ) : (
                <Button onClick={handleApply} disabled={applying || aggiornabili.length === 0} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1.5" data-testid="hr-apply-btn">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  Applica Modifiche ({aggiornabili.length})
                </Button>
              )}
            </div>

            <Tabs defaultValue="aggiornabili" className="w-full">
              <TabsList className="grid grid-cols-4 w-full">
                <TabsTrigger value="aggiornabili" className="text-xs gap-1" data-testid="hr-tab-aggiornabili"><CheckCircle2 className="w-3 h-3" /> Aggiornabili ({aggiornabili.length})</TabsTrigger>
                <TabsTrigger value="verificare" className="text-xs gap-1" data-testid="hr-tab-verificare"><AlertTriangle className="w-3 h-3" /> Da Verificare ({daVerificare.length})</TabsTrigger>
                <TabsTrigger value="tutti" className="text-xs">Tutti ({trovati.length})</TabsTrigger>
                <TabsTrigger value="nontrovati" className="text-xs gap-1" data-testid="hr-tab-nontrovati"><XCircle className="w-3 h-3" /> Non Trovati ({nonTrovati.length})</TabsTrigger>
              </TabsList>
              <TabsContent value="aggiornabili"><RowsTable data={aggiornabili} mode="full" /></TabsContent>
              <TabsContent value="verificare"><RowsTable data={daVerificare} mode="full" /></TabsContent>
              <TabsContent value="tutti"><RowsTable data={trovati} mode="full" /></TabsContent>
              <TabsContent value="nontrovati"><RowsTable data={nonTrovati} mode="nontrovati" /></TabsContent>
            </Tabs>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
